import { getDataAPI } from './fetchData';
import { store } from '../store';
import { setCacheData, setCacheLoading } from '../store/dataCache/dataCacheSlice';

// Cache TTL: 5 minutes (300000ms)
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Check if cached data is still valid
 */
const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_TTL;
};

/**
 * Preload data for a specific cache key
 */
export const preloadData = async (key, apiEndpoint, token) => {
  try {
    // Check if we already have valid cached data
    const state = store.getState();
    const cacheEntry = state.dataCache?.[key];
    
    if (cacheEntry?.data && isCacheValid(cacheEntry.timestamp)) {
      console.log(`✅ Using cached data for ${key}`);
      return cacheEntry.data;
    }

    // Set loading state
    store.dispatch(setCacheLoading({ key, loading: true }));

    console.log(`🔄 Preloading data for ${key}...`);
    const response = await getDataAPI(apiEndpoint, token);

    if (response.data.status === 200 && response.data.data) {
      const data = Array.isArray(response.data.data) 
        ? response.data.data 
        : response.data.data.data || response.data.data;
      
      // Store in cache
      store.dispatch(setCacheData({ key, data: Array.isArray(data) ? data : [] }));
      console.log(`✅ Preloaded ${key}:`, data.length || 'N/A', 'items');
      
      return data;
    }

    // If no data, cache empty array
    store.dispatch(setCacheData({ key, data: [] }));
    return [];
  } catch (error) {
    console.error(`❌ Failed to preload ${key}:`, error);
    store.dispatch(setCacheLoading({ key, loading: false }));
    
    // Return cached data if available, even if stale
    const state = store.getState();
    const cacheEntry = state.dataCache?.[key];
    if (cacheEntry?.data) {
      console.log(`⚠️ Using stale cache for ${key} due to error`);
      return cacheEntry.data;
    }
    
    return [];
  }
};

/**
 * Preload all common app data
 */
export const preloadAllData = async () => {
  const token = JSON.parse(localStorage.getItem('dizeeToken'));
  if (!token) {
    console.warn('No token available for preloading');
    return;
  }

  console.log('🚀 Starting data preload...');

  // Preload in parallel for better performance
  const preloadPromises = [
    preloadData('catalog', 'user/getCatalog', token),
    preloadData('bookings', 'user/getBookings', token),
    preloadData('merch', 'user/getMerch', token),
  ];

  // Don't await - let them load in background
  Promise.allSettled(preloadPromises).then(() => {
    console.log('✅ Data preload complete');
  });

  // Return immediately so app doesn't wait
  return Promise.resolve();
};

/**
 * Get cached data synchronously (for immediate use)
 */
export const getCachedData = (key) => {
  const state = store.getState();
  const cacheEntry = state.dataCache?.[key];
  
  if (cacheEntry?.data && isCacheValid(cacheEntry.timestamp)) {
    return cacheEntry.data;
  }
  
  // Return cached data even if stale, or null
  return cacheEntry?.data || null;
};

/**
 * Refresh data in background (updates cache)
 */
export const refreshData = async (key, apiEndpoint, token) => {
  return preloadData(key, apiEndpoint, token);
};

